import { useCallback, useState } from 'react';
import { ContentType } from '@amzn/awscat-pattern-library-service-model';

import { useContentEditorService } from '../../contexts/ContentEditorServiceContext';
import { useQualityCheckService } from '../../contexts/QualityCheckServiceContext';
import { useCurrentUser } from '../../contexts/UserContext';
import { isLockedByOther } from '../../model/LoadedDocument';
import { useContent } from '../v2-hooks/useContent';
import { useQualityCheckState } from '../v2-hooks/useQualityCheckState';
import { log } from '../../loggers/Logger';
import { ContentValidationError } from '../../services/Errors';
import { TooManyScansError } from '../../services/QualityCheckService';
import FlashbarMessageBroker from '../../common/FlashbarMessageBroker';
import { useAuthorPanelSharedState } from './AuthorStatePanelViewModel';
import { useLoadedDocument } from '../v2-hooks/useLoadedDocument';
import { ContentValidationIssue } from '../v2-hooks/useSaveContent';

export interface AuthorDraftStatePanelViewModel {
  readonly isBusy: boolean;
  readonly isSaveLoading: boolean;
  readonly isDiscardLoading: boolean;
  readonly isDirty: boolean;
  readonly errorMessage: string | undefined;
  readonly validationIssues: readonly ContentValidationIssue[] | undefined;
  readonly dismissError: () => void;
  readonly saveAndClose: () => void;
  readonly discardChanges: () => void;
  readonly startQualityCheck: () => void;
  readonly isQualityCheckLoading: boolean;
  readonly isQualityCheckDisabled: boolean;
  /**
   * When true, saving and quality check actions are disabled because
   * the document is locked by another user.
   */
  readonly isSavingDisabled: boolean;
  readonly isPatternContent: boolean;
  readonly scanErrorMessage: string | undefined;
  readonly dismissScanError: () => void;
  /** True when the last scan ran past its timeout and may still be running server-side. */
  readonly isScanTimedOut: boolean;
}

export function useAuthorDraftStatePanelViewModel(): AuthorDraftStatePanelViewModel | undefined {
  const sharedState = useAuthorPanelSharedState();
  const { setBusyAction, navigateToViewer, contentId, workflow } = sharedState;
  const service = useContentEditorService();
  const qualityCheckService = useQualityCheckService();
  const { state: qualityCheckState, results: qualityCheckResults } = useQualityCheckState();
  const content = useContent();
  const currentUser = useCurrentUser();
  const document = useLoadedDocument();

  // Errors from start quality check operations, merged with the save-flow error below.
  const [startQualityCheckError, setStartQualityCheckError] = useState<
    { readonly message: string; readonly issues: readonly ContentValidationIssue[] } | undefined
  >(undefined);

  // Depend on the stable function, not the whole sharedState object, which is a
  // fresh literal each render and would defeat this and every dependent callback's memoization.
  const { dismissError: sharedDismissError } = sharedState;

  // Dismissing clears both error sources so neither can linger behind the other.
  const dismissError = useCallback(() => {
    sharedDismissError();
    setStartQualityCheckError(undefined);
  }, [sharedDismissError]);

  const discardChanges = useCallback(async () => {
    setBusyAction('discard');
    dismissError();
    try {
      await service.closeAndDiscard({ isUnlock: true });
      navigateToViewer();
    } catch (err) {
      log.error({ err, contentId }, 'Failed to discard changes');
      FlashbarMessageBroker.create().pushErrorMessage(
        'Failed to discard changes. Please try again.',
      );
    } finally {
      setBusyAction(null);
    }
  }, [setBusyAction, dismissError, navigateToViewer, contentId, service]);

  const startQualityCheck = useCallback(async () => {
    if (!workflow || !contentId) {
      log.warn({ contentId }, 'Cannot start quality check: content not loaded');
      return;
    }
    setBusyAction('qualityCheck');
    dismissError();
    try {
      await qualityCheckService.start(contentId, workflow.draftWorkflowId);
    } catch (err) {
      // A validation failure lists exactly what the author must fix before the
      // scan can run, so surface the issues in-panel instead of a generic toast.
      if (err instanceof ContentValidationError) {
        setStartQualityCheckError({
          message: `Failed to start quality check: ${err.message}`,
          issues: err.issues,
        });
      } else if (err instanceof TooManyScansError) {
        // Throttled by the backend; the error already carries the wait-and-retry copy.
        setStartQualityCheckError({ message: err.message, issues: [] });
      } else {
        log.error({ err, contentId }, 'Failed to start quality check');
        FlashbarMessageBroker.create().pushErrorMessage(
          'Failed to start quality check. Please try again.',
        );
      }
    } finally {
      setBusyAction(null);
    }
  }, [setBusyAction, dismissError, contentId, workflow, qualityCheckService]);

  const isSavingDisabled = document ? isLockedByOther(document, currentUser.userId) : false;

  const isQualityCheckLoading =
    sharedState.busyAction === 'qualityCheck' || qualityCheckState === 'scan_in_progress';

  // A timed-out scan may still be running server-side, so block re-scanning until the
  // author reloads the page to pick up its real outcome.
  const isScanTimedOut = qualityCheckResults?.status === 'timed_out';
  const isQualityCheckDisabled =
    sharedState.isBusy ||
    qualityCheckState === 'scan_in_progress' ||
    isSavingDisabled ||
    isScanTimedOut;
  const isPatternContent = content?.contentMetadata?.contentType === ContentType.Pattern;

  const scanErrorMessage =
    qualityCheckResults?.status === 'failed'
      ? 'Scan completed with error, please try again later'
      : undefined;

  const dismissScanError = useCallback(() => {
    qualityCheckService.clearResults();
  }, [qualityCheckService]);

  // The view shows one error at a time; the save-flow error takes precedence over a
  // scan-start error since it reflects the most recent save attempt. Resolve message
  // and issues from the SAME source so the save flow (which clears only the shared
  // error) can't leave stale scan-start issues riding under a save headline.
  const activeError =
    sharedState.errorMessage !== undefined
      ? { message: sharedState.errorMessage, issues: sharedState.validationIssues }
      : startQualityCheckError;
  const errorMessage = activeError?.message;
  const validationIssues = activeError?.issues;

  // A parent-teardown render can transiently clear the document before this
  // view unmounts; return undefined so the view renders null instead of throwing.
  if (!document) return undefined;

  return {
    isBusy: sharedState.isBusy,
    isSaveLoading: sharedState.isSaveLoading,
    isDiscardLoading: sharedState.busyAction === 'discard',
    isDirty: service.isDirty,
    errorMessage,
    validationIssues,
    dismissError,
    saveAndClose: sharedState.saveAndClose,
    discardChanges,
    startQualityCheck,
    isQualityCheckLoading,
    isQualityCheckDisabled,
    isSavingDisabled,
    isPatternContent,
    scanErrorMessage,
    dismissScanError,
    isScanTimedOut,
  };
}
